import { useState } from 'react';
import clsx from 'clsx';
import * as R from 'ramda';

import DeleteModal from '@atom/components/common/DeleteModal';
import RenameModal from '@atom/components/common/RenameModal';
import renameIcon from '@atom/components/common/svgIcons/renameIcon.svg';
import InheritanceLockIcon from '@atom/components/workTemplate/InheritanceLockIcon';
import { Checkbox, Icon, Menu } from '@atom/mui';
import colors from '@atom/styles/colors';
import { FILES_ENDPOINT } from '@atom/utilities/endpoints';
import textDisplayUtilities from '@atom/utilities/textDisplayUtilities';
import { setDisplayDate } from '@atom/utilities/timeUtilities';

import classes from './workTemplateVideos.module.css';

const MenuItem = Menu.MenuItem;

const styles = {
  icon: {
    fontSize: '5rem',
    color: colors.neutral.silver,
  },
};

interface Props {
  media: any;
  renameMedia: (id: string, name: string) => void;
  deleteMedia: (id: string) => void;
  isEditDisabled: boolean;
  loading: boolean;
  isChecked: boolean;
  toggleCheckbox: (id: string) => void;
  isItemInherited?: boolean;
}

const VideoTile = ({
  media,
  renameMedia,
  deleteMedia,
  isEditDisabled,
  loading,
  isChecked,
  toggleCheckbox,
  isItemInherited = false,
}: Props) => {
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [renameModalOpen, setRenameModalOpen] = useState<boolean>(false);

  const handleRename = async (id: string, name: string) => {
    await renameMedia(id, name);
    setRenameModalOpen(false);
  };

  const handleDelete = async (id: string) => {
    await deleteMedia(id);
    setDeleteModalOpen(false);
  };

  const imageStyle = {
    backgroundImage: `url('${FILES_ENDPOINT}/${media.fileId}_180')`,
  };

  return (
    <>
      <div className={classes.photoTile}>
        <div className={classes.tileImageContainer} style={imageStyle}>
          <a
            className={classes.link}
            href={`${FILES_ENDPOINT}/${media.fileId}`}
            target="_blank"
          >
            <Icon style={styles.icon}>play_arrow</Icon>
          </a>
        </div>
        <div className={clsx(classes.imageMask, isChecked && classes.checked)}>
          <div
            className={clsx(
              classes.checkboxContainer,
              isChecked && classes.checked,
            )}
            onClick={() => toggleCheckbox(media.id)}
          >
            <Checkbox
              checked={isChecked}
              onChange={() => {}}
              style={{ color: colors.neutral.white }}
            />
          </div>
        </div>
        <div className={classes.imageInfoContainer}>
          <div className={classes.imageTitleContainer}>
            {isItemInherited && (
              <div className={classes.lockIcon}>
                <InheritanceLockIcon
                  tooltip="Inherited Video cannot be removed or edited."
                  placement="bottom-start"
                />
              </div>
            )}
            <div
              className={clsx(
                classes.imageName,
                isItemInherited && classes.disabled,
              )}
            >{`${media.name}.${media.fileExtension}`}</div>
            <Menu noMinWidth IconButtonProps={{ size: 'small' }}>
              {!isEditDisabled && !isItemInherited && (
                <MenuItem
                  onClick={() => setRenameModalOpen(true)}
                  startAdornment={<img src={renameIcon} />}
                >
                  Rename
                </MenuItem>
              )}
              <a
                href={`${FILES_ENDPOINT}/${media.fileId}`}
                download={`${media.name}.${media.fileExtension}`}
              >
                <MenuItem startAdornment={<Icon>file_download</Icon>}>
                  Download
                </MenuItem>
              </a>
              {!isEditDisabled && !isItemInherited && (
                <MenuItem
                  onClick={() => setDeleteModalOpen(true)}
                  startAdornment={<Icon>delete</Icon>}
                >
                  Delete
                </MenuItem>
              )}
            </Menu>
          </div>
          <div className={classes.additionalInfoContainer}>
            <div>
              {!R.isEmpty(media.createdBy)
                ? textDisplayUtilities.displayName(media.createdBy)
                : ''}
            </div>
            <div>{`Uploaded on ${setDisplayDate(media.createdDate)}`}</div>
          </div>
        </div>
      </div>
      <RenameModal
        id={media.id}
        name={media.name}
        type={media.type}
        renameAction={handleRename}
        open={renameModalOpen}
        closeModal={() => setRenameModalOpen(false)}
      />
      <DeleteModal
        open={deleteModalOpen}
        onConfirm={() => handleDelete(media.id)}
        onCancel={() => setDeleteModalOpen(false)}
        title={`Delete ${textDisplayUtilities.capitalize(media.type)}`}
        content={`Are you sure you want to delete this ${media.type}?`}
        loading={loading}
      />
    </>
  );
};

export default VideoTile;
